/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

import "../libextensions/jsface"; // Vite 8: ensure augmentedFace is initialized before this chunk executes
import dayjs from "dayjs/esm/index";
import localeData from "dayjs/plugin/localeData";
import customParseFormat from "dayjs/plugin/customParseFormat";

function deployGbc(contextWin) {
  // try to store js errors for forensics
  contextWin.__jsErrors = contextWin.__jsErrors || [];
  contextWin.__jsWarns = contextWin.__jsWarns || [];

  /**
   * @namespace classes
   * @alias classes
   */
  const classes = {};

  /**
   * Genero Browser Client main entry point service
   * @namespace gbc
   * @gbcService
   */
  const gbc = contextWin.augmentedFace.Singleton(
    /** @lends gbc.prototype */ {
      __name: "gbc",
      oo: contextWin.augmentedFace, // Will be removed when migrated to js native classes
      uid: null,
      buildTool: "__BUILDTOOL__",
      version: "__VERSION__",
      build: "__BUILD__",
      tag: "__TAG__",
      dirtyFlag: "__DIRTY__",
      lastCompDate: "__COMPDATE__",
      prodMode: "__PROD__",
      copyrightYear: "__YEAR__",
      systemAppId: -1,
      queryStringTheme: null,
      unitTestMode: false,
      canShowExitWarning: true,
      androidEmulationDebug: null,
      browserMultiPage: false,
      bootstrapInfo: contextWin.__gbcBootstrap || {},
      dayjs: contextWin.dayjs || dayjs,
      styler: contextWin.styler,
      StateMachine: contextWin.StateMachine,
      classes: classes,
      constants: {
        theme: {}
      },
      errorCount: 0,
      jsErrorCount: 0,
      __wrapper: contextWin.gbcWrapper,
      /**
       * prepare GBC system environment
       * @memberOf gbc
       */
      preStart: function () {
        gbc.uid = classes.StringHelper.random();
        gbc.androidEmulationDebug = classes.WindowHelper.isAndroidEmulationDebug();
        classes.DOMElementHelper.toggleClass(document.body, "is-mobile-device", classes.WindowHelper.isMobile());
        classes.DOMElementHelper.toggleClass(document.body, "is-not-mobile-device", !classes.WindowHelper.isMobile());
        classes.DOMElementHelper.toggleClass(document.body, "is-touch-device", classes.WindowHelper.isTouchDevice());
        classes.DOMElementHelper.toggleClass(document.body, "is-not-touch-device", !classes.WindowHelper.isTouchDevice());
        classes.DOMElementHelper.toggleClass(document.body, "is-firefox", contextWin.browserInfo.isFirefox);
        classes.DOMElementHelper.toggleClass(document.body, "is-edge", contextWin.browserInfo.isEdge);
        classes.DOMElementHelper.toggleClass(document.body, "is-chrome", contextWin.browserInfo.isChrome);
        classes.DOMElementHelper.toggleClass(document.body, "is-opera", contextWin.browserInfo.isOpera);
        classes.DOMElementHelper.toggleClass(document.body, "is-safari", contextWin.browserInfo.isSafari);
        classes.DOMElementHelper.toggleClass(document.body, "is-ios", classes.WindowHelper.isIOS());
        classes.DOMElementHelper.toggleClass(document.body, "is-android", classes.WindowHelper.isAndroid());
        if (contextWin.gbc.bootstrapInfo.serverVersion) {
          contextWin.gbc.bootstrapInfo.serverVersion = contextWin.gbc.bootstrapInfo.serverVersion.replace(" - Build ", "-");
        }
        contextWin.gbc.bootstrapInfo.subAppInfo = Number.parseInt(contextWin.gbc.bootstrapInfo.subAppInfo, 10) || 0;
        // Event activation changed
        contextWin.gbc.DebugService.whenActivationChanged(function (event, src, active) {
          contextWin.gbc.LogService.enableProviders(active);
        });
        contextWin.gbc.LogService.registerLogProvider(
          new classes.KeyboardPrefixedConsoleLogProvider("[KEYBOARD ]", "background: #AA6; color: #FFF"),
          "keyboard",
          "Keyboard"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.MousePrefixedConsoleLogProvider("[MOUSE    ]", "background: #A66; color: #FFF"),
          "mouse",
          "Mouse"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.UiPrefixedConsoleLogProvider("[UI       ]", "background: #015d51; color: #FFF"),
          "ui",
          "UI"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[SCHEDULER]", "background: #6A6; color: #FFF"),
          "scheduler",
          "Scheduler"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[CLIPBOARD]", "background: #8c0099; color: #FFF"),
          "clipboard",
          "Clipboard"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[FOCUS    ]", "background: #A6A; color: #FFF"),
          "focus",
          "Focus"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[gICAPI   ]", "background: #6AA; color: #FFF"),
          "gICAPI",
          "gICAPI"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.NetworkPrefixedConsoleLogProvider("[NETWORK  ]", "background: #66A; color: #FFF"),
          "networkProtocol",
          "Network"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[ServiceWorker]", "background: #66aa86; color: #FFF"),
          "sw",
          "ServiceWorker"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.PrefixedConsoleLogProvider("[INPUT]", "background: #8A6; color: #FFF"),
          "input",
          "Input"
        );
        contextWin.gbc.LogService.registerLogProvider(
          new classes.KeyboardPrefixedConsoleLogProvider("[ClientQA ]", "background: #55A; color: #FFF"),
          "ClientQA",
          "ClientQA"
        );
        contextWin.gbc.LogService.registerLogProvider(new classes.BufferedConsoleLogProvider(), null, "Default");

        // need to enable or disable debug mode explicitly
        // because DebugService can be also be activated by GAS parameters
        if (classes.WindowHelper.isURLParameterEnabled(contextWin.location.search, "debugmode")) {
          contextWin.gbc.DebugService.activate();
        } else if (classes.WindowHelper.isURLParameterDisabled(contextWin.location.search, "debugmode")) {
          contextWin.gbc.DebugService.disable();
        }

        contextWin.gbc.bootstrapInfo.gbcPath = "resources";

        // querystrings
        // activate contextmenu
        if (classes.WindowHelper.isURLParameterEnabled(contextWin.location.search, "contextmenu")) {
          gbc.ThemeService.setValue("gbc-disable-context-menu", false);
        }
        // activate mobileUI
        if (classes.WindowHelper.isURLParameterEnabled(contextWin.location.search, "mobileui")) {
          gbc.ThemeService.setValue("gbc-aui-mobileUI-default", true);
        }
        // activate browserMultiPage
        if (classes.WindowHelper.isURLParameterEnabled(contextWin.location.search, "browsermultipage")) {
          gbc.browserMultiPage = true;
        } else if (classes.WindowHelper.isURLParameterDisabled(contextWin.location.search, "browsermultipage")) {
          gbc.browserMultiPage = false;
        }

        contextWin.gbc.classes.DebugHelper.activateDebugHelpers();

        /**
         * Disable default IE behavior when pressing F1 key (which launches page toward microsoft website)
         * ref: https://agile.strasbourg.4js.com/jira/browse/ENGGCS-3879
         */
        if ("onhelp" in window) {
          // To avoid IE to display the help popup dialog on F1, we override associated 'onhelp' event
          window.onhelp = function () {
            return false;
          };
        }

        //DayJS config
        // jshint ignore:start
        if (!contextWin.dayjs) {
          contextWin.dayjs = dayjs;
        }
        contextWin.dayjs.extend(contextWin.dayjs_plugin_localeData || localeData);
        contextWin.dayjs.extend(contextWin.dayjs_plugin_customParseFormat || customParseFormat);
        // jshint ignore:end
      },

      /**
       * start the GBC system (home page or application if bootstrapped)
       */
      start: function () {
        if (contextWin.gbc.__gbcStarted) {
          return;
        }

        if (classes.WindowHelper.isURLParameterEnabled("serviceworker")) {
          gbc.LogService.sw.log("Service Worker enabled");
          if (!("serviceWorker" in navigator)) {
            gbc.LogService.sw.warn("Service Worker not available on your system > Fallback on xhr");
          } else {
            navigator.serviceWorker.register("/service-worker.js").then(
              (registration) => {
                // registered!
                gbc.SW = registration.installing || registration.waiting || registration.active;
              },
              (err) => {
                gbc.LogService.sw.error("SW registration failed!");
              }
            );
            //listen for the latest sw
            navigator.serviceWorker.addEventListener("controllerchange", async () => {
              gbc.SW = navigator.serviceWorker.controller;
            });
            //listen for messages from the service worker
            navigator.serviceWorker.addEventListener("message", this.onMessage);
          }
        }

        contextWin.gbc.__gbcStarted = true;
        gbc.HostService.preStart();
        document.body.classList.add("flexible_host_stretch_row");
        if (gbc.DebugService.isMonitorWindow()) {
          return;
        }
        contextWin.gbc.HostService.start();
      },

      onMessage: function (message) {
        //got a message from the service worker
        const { data } = message;
        gbc.LogService.sw.log("[GBC:RCV]<--", data);
        const app = gbc.SessionService.getCurrent().getCurrentApplication();
        const orders = data.VMresponse.filter((order) => order.type === "om");
        if (orders.length > 0) {
          app.dvm.manageAuiOrders(orders);
          app.protocolInterface.uaProtocol.transition();
        }
      },
      sendMessage: function (msg) {
        //send some structured-cloneable data from the webpage to the sw
        if (navigator.serviceWorker.controller) {
          gbc.LogService.sw.log("[GBC:SEND]-->", msg);
          navigator.serviceWorker.controller.postMessage(msg);
        } else {
          throw new Error("Service Worker API not available");
        }
      },

      /**
       * GBC system entry point
       */
      run: function (callback) {
        if (!gbc.__initialized) {
          modulum.assemble();
          const queryStringTheme = contextWin.location.search.match(/^(?:.*[?&])?theme=([^&$]+)(?:&.*)?$/);
          gbc.queryStringTheme = queryStringTheme?.[1];

          // handle SSO queryString
          const queryStringSSO = contextWin.location.search.match(/^(?:.*[?&])?gnonce=([^&$]+)(?:&.*)?$/);
          if (queryStringSSO && history.pushState) {
            // create new URL by removing &gnonce=XXX
            const params = new URLSearchParams(contextWin.location.search); // jshint ignore:line
            params.toString();
            params.delete("gnonce");
            let newQueryStr = params.toString().length > 0 ? "?" : "";
            newQueryStr = newQueryStr + params.toString();
            const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + newQueryStr;
            window.history.pushState(
              {
                path: newurl
              },
              "",
              newurl
            ); //or use replaceState to update the history stack
          }

          //initTheme use uninitialized services (StoredSettingsService, LocalSettingsService, ...)
          gbc.ThemeService.initTheme(gbc.queryStringTheme, function () {
            gbc.InitService.initServices();
            gbc.preStart();
            const start = function () {
              gbc.start();
            };
            if (gbc.classes.ObjectHelper.isFunction(contextWin.__gbcDefer)) {
              contextWin.__gbcDefer(start);
            } else {
              contextWin.requestAnimationFrame(start);
            }
            gbc.__initialized = true;
            if (typeof callback === "function") {
              callback();
            }
          });
        }
      },
      /**
       * display before ending warning if needed when quitting the web page
       * @return {string}
       */
      showExitWarning: function () {
        if (
          gbc.ThemeService.getValue("gbc-disable-ending-popup") !== true &&
          !contextWin.__desactivateEndingPopup &&
          !classes.WindowHelper.isURLParameterEnabled(contextWin.location.search, "noquitpopup") &&
          !contextWin.gbc.DebugService?.isActive() &&
          gbc.SessionService.getCurrent() &&
          gbc.SessionService?.getCurrent().getCurrentApplication()
        ) {
          return (
            "The Genero application is still running.\n" +
            "If you leave now, some data may be lost.\n" +
            "Please use the application user interface to exit the application before navigating away from this page."
          );
        }
      },
      /**
       * Build a system-widget modal shared by {@link alert} and {@link confirm}: header,
       * a pre-wrap content area wired as the `aria-describedby` target, `role=alertdialog`,
       * and the footer button widgets. Callers arrange the footer, wire clicks/keys and
       * call `show()`. The returned `destroy()` tears down the buttons AND the modal —
       * buttons are attached via `setParentWidget` (not `addChildWidget`), so the modal's
       * own `destroyChildren()` would not reach them and they would otherwise leak.
       * @param {string} text - message shown to the user
       * @param {string} header - dialog title
       * @param {Array<{text: string}>} buttonSpecs - one entry per footer button
       * @returns {{modal: *, buttonWidgets: Array<*>, destroy: Function}}
       * @ignore
       */
      _buildSystemChoiceModal: function (text, header, buttonSpecs) {
        const modal = contextWin.gbc.classes.WidgetFactory.createWidget("Modal", { appHash: gbc.systemAppId, isSystemWidget: true });
        modal.addClasses("gbc_alert");
        modal.setHeader(header);
        modal.setClosable(false);

        const contents = document.createElement("div");
        contents.id = "w_" + modal.getUniqueIdentifier() + "_desc";
        contents.setAttribute("style", "white-space: pre-wrap;");
        contents.textContent = text;
        modal.setContent(contents);
        modal.setAriaAttribute("describedby", contents.id);

        const buttonWidgets = buttonSpecs.map((spec) => {
          const button = contextWin.gbc.classes.WidgetFactory.createWidget("Button", { appHash: gbc.systemAppId });
          button.setText(spec.text);
          button.setParentWidget(modal);
          return button;
        });

        const destroy = () => {
          for (const button of buttonWidgets) {
            button.destroy();
          }
          modal.destroyChildren();
          modal.destroy();
        };

        return { modal, buttonWidgets, destroy };
      },
      /**
       * Emulated window.alert
       * @param {string} text
       * @param {string} header
       * @param {Function} [okCallback]
       * @ignore
       */
      alert: function (text, header, okCallback = null) {
        return new Promise((resolve) => {
          const { modal, buttonWidgets, destroy } = gbc._buildSystemChoiceModal(text, header, [{ text: "Ok" }]);
          const okButton = buttonWidgets[0];
          modal.setFooter(okButton.getElement());
          document.body.appendChild(modal.getElement());

          modal.manageKeyDown = function (keyString) {
            if (keyString === "enter" || keyString === "return") {
              okButton.emit(gbc.constants.widgetEvents.click);
              return true;
            }
            if (keyString === "esc") {
              // esc dismisses WITHOUT running okCallback (e.g. ClipboardService treats it as "declined")
              destroy();
              resolve();
              return true;
            }
            return false;
          };

          modal.show();

          okButton.when(gbc.constants.widgetEvents.click, async () => {
            destroy();
            if (okCallback) {
              await okCallback();
            }
            resolve();
          });
        });
      },
      /**
       * Emulated modal confirmation dialog with arbitrary choice buttons.
       * Generalizes {@link alert} (the single-"Ok"-button case): pass any number of
       * buttons and the promise resolves with the `value` of the one the user picks.
       * The dialog is a system widget, so it works in every context (top-level and
       * child programs) and inherits the modal focus-trap and dialog ARIA semantics.
       * @param {string} text - message shown to the user
       * @param {string} header - dialog title
       * @param {Array<{text: string, value: *, isDefault: boolean, isCancel: boolean}>} buttons
       *        footer buttons; the button flagged `isDefault` is triggered by Enter,
       *        the one flagged `isCancel` by Escape.
       * @returns {Promise<*>} the value of the chosen button (the `isCancel` value on Escape)
       * @ignore
       */
      confirm: function (text, header, buttons) {
        return new Promise((resolve) => {
          if (!buttons || buttons.length === 0) {
            resolve(undefined);
            return;
          }
          const { modal, buttonWidgets, destroy } = gbc._buildSystemChoiceModal(text, header, buttons);
          // a confirm dialog interrupts the user for a decision -> alertdialog (it also
          // receives initial focus below); gbc.alert stays the plain dialog role.
          modal.setAriaRole("alertdialog");
          const choose = (value) => {
            destroy();
            resolve(value);
          };

          const footer = document.createElement("div");
          // buttons side by side, right-aligned, sized to content (a .gbc_ButtonWidget is
          // display:flex, i.e. a full-width block on its own)
          footer.setAttribute("style", "display: flex; flex-direction: row; justify-content: flex-end; gap: 8px;");
          for (const [i, button] of buttonWidgets.entries()) {
            button.getElement().style.flex = "0 0 auto";
            footer.appendChild(button.getElement());
            button.when(gbc.constants.widgetEvents.click, () => choose(buttons[i].value));
          }
          modal.setFooter(footer);
          document.body.appendChild(modal.getElement());

          modal.manageKeyDown = function (keyString) {
            // Enter triggers the isDefault button, Escape the isCancel one; any other key
            // (or a set with no matching flag) leaves the dialog open.
            if (keyString === "enter" || keyString === "return") {
              const def = buttons.find((b) => b.isDefault);
              if (def) {
                choose(def.value);
                return true;
              }
            }
            if (keyString === "esc") {
              const cancel = buttons.find((b) => b.isCancel);
              if (cancel) {
                choose(cancel.value);
                return true;
              }
            }
            return false;
          };

          modal.show();

          // place focus on the default button so keyboard / screen-reader users land inside
          // the dialog (WAI-ARIA dialog guidance), in both accessibility and normal modes
          const defIndex = buttons.findIndex((b) => b.isDefault);
          if (defIndex >= 0) {
            const el = buttonWidgets[defIndex].getElement();
            (el.querySelector("button, [tabindex]") || el).focus();
          }
        });
      },
      /**
       * return information regarding the gbc version, build number,
       * gas version, fgl version and platform used by the current application
       * If an app is given, read the content of the object
       * @param {classes.VMApplicationInfo?} app
       */
      info: function (app = null) {
        const origin = app || gbc.SessionService.getCurrent()?.getCurrentApplication().applicationInfo;
        const {
          connectionInfo: { runtimeVersion }
        } = origin || {
          connectionInfo: {
            runtimeVersion: ""
          }
        };
        const { serverVersion } = origin || {
          serverVersion: ""
        };

        const platform = contextWin.gbcWrapper.platformName;

        return {
          gbcVersion: gbc.version,
          buildNumber: gbc.build,
          serverVersion,
          runtimeVersion,
          platform
        };
      }
    }
  );

  const rawError = function () {
    const args = Array.prototype.join.call(arguments, " ");
    contextWin.__jsErrors.push(args);
    console.error(args);
  };

  const stackCallback = function (stackframes) {
    rawError("ERROR - Stacktrace");
    const stringifiedStack =
      "    at " +
      stackframes
        .map(function (sf) {
          return sf.toString();
        })
        .join("\n    at ");
    rawError(stringifiedStack);
  };

  const stackErrorCallback = function (err) {
    rawError(err);
  };

  /**
   * log and stacktrace errors of the GBC system
   * @param errorText
   * @param error
   * @param _fromNativeCall
   * @ignore
   */
  gbc.error = function (errorText, error, _fromNativeCall) {
    const text = typeof errorText === "string" ? errorText : error?.toString?.();
    const err = error ?? errorText?.stack;
    rawError(text);
    if (!_fromNativeCall) {
      gbc.__wrapper.nativeCall(
        gbc.__wrapper.param({
          name: "error",
          args: [text]
        })
      );
    }
    if (contextWin.StackTrace && err && typeof err !== "string") {
      contextWin.StackTrace.fromError(err).then(stackCallback).catch(stackErrorCallback);
    }
  };

  /**
   * log and stacktrace warning of the GBC system
   * @ignore
   */
  gbc.warn = function () {
    const args = Array.prototype.join.call(arguments, " ");
    contextWin.__jsWarns.push(args);
    console.warn(args);
  };

  /**
   *
   * @param msg
   * @param file
   * @param line
   * @param col
   * @param error
   * @ignore
   */
  contextWin.onerror = function (msg, file, line, col, error) {
    gbc.error(msg, error);
    if (contextWin.gbc?.classes?.WindowHelper?.isURLParameterEnabled(contextWin.location.search, "debugmode")) {
      window.critical.display(msg);
    }
  };

  /**
   * js stacktrace emulation
   * @ignore
   */
  gbc.stack = function () {
    if (contextWin.StackTrace) {
      contextWin.StackTrace.generateArtificially().then(stackCallback).catch(stackErrorCallback);
    }
  };

  contextWin.addEventListener("unload", function () {
    let i = 0;
    const sessions = gbc?.SessionService?.getSessions(),
      len = sessions?.length;
    if (len) {
      for (; i < len; i++) {
        if (sessions[i]) {
          sessions[i].closeSession(true);
        }
      }
    }
    gbc.InitService.emit(gbc.constants.widgetEvents.onUnload);
    // Not sure that the debug service exists at this moment
    gbc.DebugService?.destroy();
    gbc.InitService.destroy();
    document.body.innerHTML = "";
  });

  /**
   * @ignore
   * @return {*|string}
   */
  contextWin.onbeforeunload = function () {
    //emit hook
    gbc.InitService.emit(gbc.constants.widgetEvents.onBeforeUnload);
    gbc.LogService?.ui.log("window onBeforeUnload called");

    // @note remove this as this is no more supported
    if (gbc.canShowExitWarning) {
      // Deprecated since Chrome 51 : https://www.chromestatus.com/feature/5349061406228480
      return gbc.showExitWarning();
    }
  };

  /**
   * @ignore
   * @return {*|string}
   */
  contextWin.onblur = function () {
    //emit hook
    if (gbc.InitService && gbc.constants.widgetEvents) {
      gbc.InitService.emit(gbc.constants.widgetEvents.onBlur);
    }
  };

  /**
   * @ignore
   * @return {*|string}
   */
  contextWin.onpopstate = function () {
    // need to check first that current app is a main app
    if (!gbc.SessionService.getCurrent().getCurrentApplication().getProcIdParent()) {
      // raise applicationStateChanged command if this is a main app
      gbc.SessionService.getCurrent().getCurrentApplication().scheduler?.applicationStateChangedCommand();
    }
  };

  contextWin.modulum.inject(gbc, gbc.classes);

  /**
   * testing purpose only
   * @param callback
   * @ignore
   * @private
   */
  gbc.__isIdleTest = function (callback) {
    try {
      const session = contextWin.gbc?.SessionService?.getCurrent?.();
      const qaReady = gbc?.QAService?.isQAReady?.();
      callback({
        session: Boolean(session),
        idle: !session?.getCurrentApplication() || session?.isCurrentIdle(),
        qaReady: qaReady,
        jsErrors: window.__jsErrors,
        jsWarns: window.__jsWarns
      });
    } catch (e) {
      const result = {
        ___TEST_EXCEPTION: true
      };
      result.message = e.toString();
      result.stack = e.stack?.toString();
      callback(result);
    }
  };

  if (contextWin.gbc?.classes?.WindowHelper?.isURLParameterEnabled(contextWin.location.search, "unittestmode")) {
    gbc.unitTestMode = true;
  }

  contextWin.gbc = gbc;

  contextWin.gbcWrapper.__init();
}

deployGbc(window);
